import {
	FxIdSdkAdapterSocialSettingDefault,
	FxIdSdkBaseAdapter,
	IExportedFxIdSdkMethod,
	IFxIdSdkAdapterBuyProductRequest,
	IFxIdSdkAdapterBuyProductResponse,
	IFxIdSdkAdapterInfoSocial,
	IFxIdSdkAdapterSocialSettings,
	IFxIdSdkAdapterStatEventRequest,
	IFxIdSdkAdapterStatInitializeRequest
} from "./FxIdSdkBaseAdapter";
import { deferred } from "./FxIdSdkUtils";
import OpenApiClient from "../Api/OpenApiClient";
import {
	IVkExternalApi,
	IVkExternalApiError,
	IVkGetStatusCallbackResult,
	IVkIframeApiCallbacks,
	IVkMerchantParams,
	IVkUserInfoCallbackResult,
	VkLoginStatusUserAuthorizedNotRegistered,
	VkLoginStatusUserNotAuthorized
} from "../vkplay";
import {
	FxIdApplicationStoreCreatePaymentHandlerEmbeddingType,
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxGamesLibWebPublicDataBase,
	FxIdWebFeaturesStoreCreatePaymentRequest
} from "../Api/gen";
import { sharingHandlersStore, userStore } from "../Stores";
import i18next from "i18next";
import { getAuthToken } from "@app/Api/Auth";
import { IframeType } from "@app/Screens/WebPlayer/Hooks";
import { FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus } from "../Api/gen";
import {
	IPlatformPurchaseContext,
	IPlatformPurchaseDescriptor,
	PlatformPurchaseError,
	PlatformPurchaseStep,
	runPlatformPurchaseStep
} from "./PlatformPurchaseError";

/** VK Play не передаёт ошибку, только факт закрытия окна оплаты. */
const VK_PLAY_PAYMENT_WINDOW_CLOSED = { callback: "paymentWindowClosedCallback" } as const;

/** Правила диагностики покупки через iframeApi VK Play. */
const VK_PLAY_PURCHASE: IPlatformPurchaseDescriptor = {
	platform: "vkplay",
	source: "vkplay",
	isCancellation: isVkPlayCancellation,
	errorText: vkPlayErrorText,
	sourceCode: vkPlaySourceCode
};

/**
 * Распознаёт отмену только по сигналу закрытия окна на шаге оплаты.
 * @param step шаг покупки
 * @param error исходная ошибка или сигнал iframeApi
 */
function isVkPlayCancellation(step: PlatformPurchaseStep, error: unknown): boolean {
	return step === "purchase" && error === VK_PLAY_PAYMENT_WINDOW_CLOSED;
}

/**
 * Даёт понятный игре текст для сигнала закрытия окна без ошибки.
 * @param error исходная ошибка или сигнал iframeApi
 */
function vkPlayErrorText(error: unknown): string | undefined {
	return error === VK_PLAY_PAYMENT_WINDOW_CLOSED ? "User closed payment window" : undefined;
}

/**
 * Возвращает код сигнала закрытия окна оплаты VK Play
 * @param error исходная ошибка или сигнал iframeApi
 */
function vkPlaySourceCode(error: unknown): string | undefined {
	return error === VK_PLAY_PAYMENT_WINDOW_CLOSED ? "paymentWindowClosedCallback" : undefined;
}

/** Покупка, для которой ещё не получен первый завершающий сигнал iframeApi. */
interface IPendingVkPlayPurchase {
	context: IPlatformPurchaseContext & { transactionId: string };
	result: IFxIdSdkAdapterBuyProductResponse;
	resolve: (result: IFxIdSdkAdapterBuyProductResponse) => void;
	reject: (error: PlatformPurchaseError) => void;
}

export class FxIdSdkAdapterForVkPlay extends FxIdSdkBaseAdapter implements IVkIframeApiCallbacks {
	readonly adapter: IframeType = "vkplay";
	readonly defaultSocialSettings: IFxIdSdkAdapterSocialSettings = {
		...FxIdSdkAdapterSocialSettingDefault,
		ui: {
			...FxIdSdkAdapterSocialSettingDefault.ui,
			disabled: false
		}
	};
	vkExternalApi?: IVkExternalApi;
	private pendingPurchase?: IPendingVkPlayPurchase;
	private userInfoCallbackResolve?: (data: IVkUserInfoCallbackResult) => void;
	private userInfoCallbackReject?: (reason?: IVkExternalApiError) => void;
	private getLoginStatusCallbackResolve?: (data: IVkGetStatusCallbackResult) => void;
	private getLoginStatusCallbackReject?: (reason?: IVkExternalApiError) => void;
	appid!: number;
	private registerUserCallbackResolve?: (data: IVkUserInfoCallbackResult) => void;
	private registerUserCallbackReject?: (reason?: IVkExternalApiError) => void;
	private userInfo?: IFxIdSdkAdapterInfoSocial;

	public static VkPlayHost = "vkplay.ru";

	constructor(
		protected exportedSdk: IExportedFxIdSdkMethod,
		protected game: string,
		protected config: FxGamesLibWebPublicDataBase
	) {
		super(exportedSdk, game, config);
		const appId = config.PlatformData.VkontaktePlay?.AppId;

		if (appId == null) {
			throw new Error("No AppId for VkontaktePlay adapter");
		}
		this.appid = appId;
	}

	RegisterShareHandlers(): Promise<void> {
		// Это все залипуха. Надо будет менять на нормальные апи
		sharingHandlersStore.getState().enableShareVk(() => {
			const url = `https://vk.com/share.php?url=${window.location}&title=${i18next.t(`seo.${this.game}.title`)}`;
			window.open(url, "__blank");
		});

		sharingHandlersStore.getState().enableShareOk(() => {
			const url = `https://connect.ok.ru/offer?url=${window.location}&title=${i18next.t(
				`seo.${this.game}.title`
			)}`;
			window.open(url, "__blank");
		});

		sharingHandlersStore.getState().enableShareTelegram(() => {
			const url = `https://telegram.me/share/url?url=${window.location}&text=${i18next.t(
				`seo.${this.game}.title`
			)}`;
			window.open(url, "__blank");
		});
		return Promise.resolve();
	}

	/**
	 * Создаёт заказ FX ID и ждёт первого завершающего сигнала окна оплаты VK Play.
	 * @param request товар и данные, переданные игрой
	 */
	async BuyProduct(request: IFxIdSdkAdapterBuyProductRequest): Promise<IFxIdSdkAdapterBuyProductResponse> {
		const url = new URL(window.location.href);
		const currency =
			(url.searchParams.get("currency") as keyof IVkMerchantParams["merchant_param"]["currency"]) ?? "RUB";
		const paymentRequest: FxIdWebFeaturesStoreCreatePaymentRequest = {
			Token: getAuthToken()!,
			Game: this.game,
			Sku: request.sku,
			WebPublishingPlatform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontaktePlay,
			EmbeddingType: FxIdApplicationStoreCreatePaymentHandlerEmbeddingType.Embed,
			Currency: currency,
			VkontaktePlay: {
				IpAddress: "0.0.0.0",
				Uid: this.userInfo?.userId ?? url.searchParams.get("uid") ?? "",
				Currency: currency
			},
			ProductDescriptionHint: request.productDescriptionHint,
			ProductNameHint: request.productNameHint
		};

		if (request.developerPayload != null) {
			paymentRequest.DeveloperPayload = {
				MerchantDeveloperPayload: request.developerPayload
			};
		}

		const context: IPlatformPurchaseContext = { sku: request.sku };
		const createPaymentResult = await runPlatformPurchaseStep(
			VK_PLAY_PURCHASE,
			"create_payment",
			context,
			() => OpenApiClient.Store.fxIdWebFeaturesStoreCreatePaymentEndpoint(paymentRequest)
		);

		log.info("Received result from server: %o", createPaymentResult);

		const { promise, resolve, reject } = deferred<IFxIdSdkAdapterBuyProductResponse>();
		this.pendingPurchase = {
			context: { ...context, transactionId: createPaymentResult.TransactionId },
			result: {
				transactionId: createPaymentResult.TransactionId,
				stats: {
					currency: createPaymentResult.OrderProduct.Currency ?? "",
					currencyAmount: createPaymentResult.OrderProduct.Price ?? 0,
					usdAmount: createPaymentResult.OrderProduct.UsdPrice ?? 0
				}
			},
			resolve,
			reject
		};

		const paymentFrameParams: IVkMerchantParams = {
			merchant_param: {
				amount: createPaymentResult.OrderPrice,
				description: createPaymentResult.OrderProduct.LocalizedProductDescription,
				currency: currency,
				public_order_id: createPaymentResult.TransactionId
			}
		};

		log.info("Requesting payment frame with params", paymentFrameParams);
		try {
			if (this.vkExternalApi === undefined) {
				void this.FailPurchase(new Error("VK Play iframe API is not initialized"));
			} else {
				this.vkExternalApi.paymentFrame(paymentFrameParams);
			}
		} catch (failure) {
			void this.FailPurchase(failure);
		}

		return promise;
	}

	/** Завершает оплату один раз; последующие события игнорируются ещё до optimistic HTTP. */
	private async CompletePurchase(): Promise<void> {
		const pendingPurchase = this.pendingPurchase;
		this.pendingPurchase = undefined;
		if (pendingPurchase === undefined) {
			return;
		}

		log.info("Buy product resolved");
		try {
			await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint({
				PublicOrderId: pendingPurchase.context.transactionId,
				Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Completed
			});
		} catch (error) {
			log.error("[VKPLAY] Failed to set optimistic status:", error);
		}
		pendingPurchase.resolve(pendingPurchase.result);
	}

	/**
	 * Отклоняет покупку один раз, сохраняя исходную причину при сбое optimistic HTTP.
	 * @param failure ошибка iframeApi или сигнал закрытия окна
	 */
	private async FailPurchase(failure: unknown): Promise<void> {
		const pendingPurchase = this.pendingPurchase;
		this.pendingPurchase = undefined;
		if (pendingPurchase === undefined) {
			return;
		}

		log.info("Buy product rejected %o", failure);
		try {
			await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint({
				PublicOrderId: pendingPurchase.context.transactionId,
				Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Cancelled
			});
		} catch (error) {
			log.error("[VKPLAY] Failed to set optimistic status:", error);
		}
		pendingPurchase.reject(
			PlatformPurchaseError.fromError(VK_PLAY_PURCHASE, "purchase", pendingPurchase.context, failure)
		);
	}

	GetSocialInfo(): Promise<IFxIdSdkAdapterInfoSocial> {
		const { promise, resolve, reject } = deferred<IFxIdSdkAdapterInfoSocial>();
		this.userInfoCallbackResolve = (data: IVkUserInfoCallbackResult) => {
			resolve({
				social: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontaktePlay,
				paymentsAvailable: true,
				userId: data.uid.toString(),
				capabilities: []
			});
		};
		this.userInfoCallbackReject = reject;

		this.vkExternalApi?.userInfo();

		return promise;
	}

	GetLoginStatus(): Promise<IVkGetStatusCallbackResult> {
		log.info("Requesting login status");
		const { promise, resolve, reject } = deferred<IVkGetStatusCallbackResult>();
		this.getLoginStatusCallbackResolve = resolve;
		this.getLoginStatusCallbackReject = reject;

		this.vkExternalApi?.getLoginStatus();

		return promise;
	}

	RegisterUser(): Promise<IVkUserInfoCallbackResult> {
		log.info("Requesting register user");
		const { promise, resolve, reject } = deferred<IVkUserInfoCallbackResult>();
		this.registerUserCallbackResolve = resolve;
		this.registerUserCallbackReject = reject;

		this.vkExternalApi?.registerUser();

		return promise;
	}

	async Initialize(): Promise<void> {
		log.info("Initializing VkPlay adapter");

		await new Promise<void>((resolve) => {
			const s = document.createElement("script");
			s.type = "text/javascript";
			s.src = `//vkplay.ru/app/${this.appid}/static/mailru.core.js`;
			s.addEventListener(
				"load",
				function (e) {
					log.info("Script loaded");
					resolve();
				},
				false
			);
			const head = document.getElementsByTagName("head")[0];
			head.appendChild(s);

			log.info("Script added");
		});

		log.info("Initializing api");
		this.vkExternalApi = await window.iframeApi({
			appid: this.appid,
			getLoginStatusCallback: this.getLoginStatusCallback.bind(this),
			userInfoCallback: this.userInfoCallback.bind(this),
			userProfileCallback: this.userProfileCallback.bind(this),
			registerUserCallback: this.registerUserCallback.bind(this),
			paymentFrameUrlCallback: this.paymentFrameUrlCallback.bind(this),
			getAuthTokenCallback: this.getAuthTokenCallback.bind(this),
			paymentReceivedCallback: this.paymentReceivedCallback.bind(this),
			paymentWindowClosedCallback: this.paymentWindowClosedCallback.bind(this),
			userConfirmCallback: this.userConfirmCallback.bind(this)
		});
		log.info("Api initialized");

		const loginStatus = await this.GetLoginStatus();

		log.info("Received login status: " + loginStatus);

		if (loginStatus.loginStatus === VkLoginStatusUserNotAuthorized) {
			this.vkExternalApi.authUser();

			// https://documentation.vkplay.ru/f2p_vkp/f2pb_js_vkp#authUser
			// Авторизация пользователя. При вызове метода появляется окно авторизации.
			// После авторизации страница будет перезагружена, далее можно запросить статус и информацию о пользователе.
			await new Promise((resolve) => {
				log.info("Eternal promise. VkPlay MUST reload page");
			});
			document.location.reload();
		} else if (loginStatus.loginStatus === VkLoginStatusUserAuthorizedNotRegistered) {
			await this.RegisterUser();
			this.vkExternalApi?.reloadWindow();
			document.location.reload();
		}

		this.userInfo = await this.GetSocialInfo();

		userStore.getState().updateUserId(this.userInfo.userId);
		userStore.getState().updateDisplayName(`#${this.userInfo.userId}`);
	}

	StoreCurrency(): Promise<string | undefined> {
		const url = new URL(window.location.href);
		const currency =
			(url.searchParams.get("currency") as keyof IVkMerchantParams["merchant_param"]["currency"]) ?? "RUB";

		return Promise.resolve(currency);
	}

	/**
	 * Принимает подтверждение оплаты; UID платформы не заменяет номер заказа FX ID.
	 * @param _data данные пользователя от VK Play
	 */
	paymentReceivedCallback(_data: { uid: string }): void {
		log.info("paymentReceivedCallback");
		void this.CompletePurchase();
	}

	/** Принимает закрытие окна как отмену, только если оплата ещё не подтверждена. */
	paymentWindowClosedCallback(): void {
		log.info("paymentWindowClosedCallback");
		void this.FailPurchase(VK_PLAY_PAYMENT_WINDOW_CLOSED);
	}

	userInfoCallback = (param: IVkUserInfoCallbackResult | IVkExternalApiError): void => {
		log.info("userInfoCallback", param);
		if (param.status === "error") {
			this.userInfoCallbackReject?.call(this, param);
		} else {
			this.userInfoCallbackResolve?.call(this, param);
		}
	};

	getLoginStatusCallback = (param: IVkGetStatusCallbackResult | IVkExternalApiError): void => {
		log.info("getLoginStatusCallback", param);
		if (param.status === "error") {
			this.getLoginStatusCallbackReject?.call(this, param);
		} else {
			this.getLoginStatusCallbackResolve?.call(this, param);
		}
	};

	registerUserCallback = (param: IVkUserInfoCallbackResult | IVkExternalApiError): void => {
		log.info("registerUserCallback", param);
		if (param.status === "error") {
			this.registerUserCallbackReject?.call(this, param);
		} else {
			this.registerUserCallbackResolve?.call(this, param);
		}
	};

	userProfileCallback = (profile: any) => {
		log.info("userProfileCallback", profile);
	};

	paymentFrameUrlCallback = (url: any): void => {
		log.info("paymentFrameUrlCallback", url);
	};

	getAuthTokenCallback = (token: string): void => {
		log.info("getAuthTokenCallback", token);
	};

	userConfirmCallback = (): void => {
		log.info("userConfirmCallback");
	};

	StatInitialize(request: IFxIdSdkAdapterStatInitializeRequest): Promise<void> {
		return Promise.resolve(undefined);
	}

	StatEvent(request: IFxIdSdkAdapterStatEventRequest): Promise<void> {
		return Promise.resolve(undefined);
	}
}
